import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/ActivationChecklist.css';

const API_URL = '/api';
const DISMISS_KEY = 'daress_activation_checklist_hidden';

const STEP_ICONS = {
  account_created: '👤',
  course_created:  '📚',
  lecture_added:   '🎬',
  student_joined:  '👨‍🎓',
  exam_created:    '📝',
};

const ActivationChecklist = () => {
  const [data, setData]     = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === 'true'
  );
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const fetchChecklist = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const res = await fetch(`${API_URL}/teacher/activation-checklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json();
      setData(json);
    } catch {
      // Silent — checklist is non-critical
    }
  }, []);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const handleCopyCode = () => {
    const code = data?.courseCode;
    if (code) {
      navigator.clipboard.writeText(code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  // Normalize field names — API may return different shapes
  const getProgress = (d) => d?.progress_percent ?? d?.progress ?? 0;
  const getStatus   = (d) => d?.activation_status ?? d?.status ?? 'registered';
  const getSteps    = (d) => {
    const arr = d?.steps ?? d?.checklist ?? [];
    return arr.map(s => ({ ...s, completed: s.completed ?? s.done ?? false }));
  };

  const userRole = localStorage.getItem('userRole');
  if (userRole !== 'teacher') return null;
  if (!data) return null;
  if (dismissed) return null;

  const progress = getProgress(data);
  const status   = getStatus(data);
  const steps    = getSteps(data);

  if (status === 'activated' && progress >= 100) return null;

  const completedCount = steps.filter(s => s.completed).length;

  return (
    <div className="acl-card" dir="rtl">
      {/* Header */}
      <div className="acl-header">
        <div className="acl-header-text">
          <h3 className="acl-title">أكمل إعداد حسابك</h3>
          <p className="acl-subtitle">
            أنجز هذه الخطوات ليصبح حسابك جاهزًا لاستقبال الطلاب.
          </p>
        </div>
        <button className="acl-dismiss-btn" onClick={handleDismiss} aria-label="إخفاء">
          ✕
        </button>
      </div>

      {/* Progress */}
      <div className="acl-progress-section">
        <div className="acl-progress-meta">
          <span className="acl-progress-text">{completedCount} من {steps.length} خطوات مكتملة</span>
          <span className="acl-progress-pct">{progress}%</span>
        </div>
        <div
          className="acl-progress-track"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="acl-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Steps */}
      <ul className="acl-steps">
        {steps.map((step, idx) => (
          <li key={step.key} className={`acl-step ${step.completed ? 'acl-step--done' : 'acl-step--todo'}`}>
            <div className="acl-step-indicator">
              {step.completed
                ? <span className="acl-step-check">✓</span>
                : <span className="acl-step-num">{idx + 1}</span>
              }
            </div>
            <div className="acl-step-body">
              <span className="acl-step-emoji">{STEP_ICONS[step.key] || '📌'}</span>
              <span className="acl-step-label">{step.label}</span>
            </div>
            {!step.completed && step.link && (
              <button className="acl-step-cta" onClick={() => navigate(step.link)}>
                ابدأ
              </button>
            )}
          </li>
        ))}
      </ul>

      {/* Course code — shown when course exists but no student has joined yet */}
      {data.courseCode && !steps.find(s => s.key === 'student_joined')?.completed && (
        <div className="acl-code-strip">
          <span className="acl-code-label">شارك كود الدورة مع طلابك:</span>
          <div className="acl-code-row">
            <code className="acl-code-value">{data.courseCode}</code>
            <button className="acl-copy-btn" onClick={handleCopyCode}>
              {copied ? '✅ تم' : '📋 نسخ'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationChecklist;
